import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLineChat } from '../hooks/useLineChat';
import { playNotificationSound } from '../lib/soundUtils';
import './LineChat.css';

interface LazyImageProps {
    src: string;
    alt: string;
    style?: React.CSSProperties;
    onClick?: (e: React.MouseEvent) => void;
}

interface LineChatPanelProps {
    user: any;
    onClose?: () => void;
    onReload?: () => void;
    readMode?: boolean;
}

import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ArchiveIcon from '@mui/icons-material/Archive';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { IconButton, Menu, MenuItem, Tooltip, Box, Typography, Dialog, DialogTitle, DialogContent, Button, Divider, Avatar, Snackbar, Alert, CircularProgress } from '@mui/material';
import { reservationSyncService } from '../services/reservationSyncService';
import TemplateHelpDialog from './TemplateHelpDialog';

const LazyImage: React.FC<LazyImageProps> = ({ src, alt, style, onClick }) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const imgRef = useRef(null);

    const loadImage = useCallback(() => {
        if (!src) return;
        const img = new Image();
        img.src = src;
        img.onload = () => {
            setLoaded(true);
            setError(false);
        };
        img.onerror = () => {
            setError(true);
        };
    }, [src]);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting && !loaded) {
                    loadImage();
                    observer.unobserve(entry.target);
                }
            });
        }, { threshold: 0.1 });

        if (imgRef.current) observer.observe(imgRef.current);
        return () => observer.disconnect();
    }, [src, loaded, loadImage]);

    if (error) {
        return (
            <div 
                style={{ 
                    ...style, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    background: '#fee2e2', 
                    color: '#b91c1c',
                    fontSize: '12px',
                    cursor: 'pointer',
                    minHeight: '100px',
                    padding: '10px',
                    textAlign: 'center'
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    loadImage();
                }}
            >
                [image load failed] (click to retry)
            </div>
        );
    }

    if (!loaded) {
        return (
            <div 
                ref={imgRef}
                onClick={(e) => {
                    e.stopPropagation();
                    loadImage();
                }}
                style={{ 
                    ...style, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    background: '#f1f5f9', 
                    color: '#64748b',
                    fontSize: '12px',
                    cursor: 'pointer',
                    minHeight: '100px'
                }}
            >
                [image] (click to load)
            </div>
        );
    }

    return <img src={src} alt={alt} style={style} loading="lazy" onError={() => setError(true)} onClick={onClick} />;
};

const LineChatPanel: React.FC<LineChatPanelProps> = ({ user, onClose, onReload, readMode }) => {
    const lineUserId = user?.line_user_id;
    const reservationId = user?.instance_reservation_id || user?.reservation_id;
    const instanceResData = user?.instance_res_data;
    const {
        messages, optimisticMessages, fetchMessages, fetchMessageContext, sendMessage, uploadImage,
        markRead, updateProfile, users, loading, fetchUsers,
        tags, fetchTags, addTagMaster, deleteTagMaster,
        linkReservation, unlinkReservation, toggleArchive,
        fetchTemplates, fetchTemplateCategories,
        translateMessage, sendTranslatedMessage,
        agentCheckReservation, agentCheckChange, agentCancelReservation,
        suggestReply,
    } = useLineChat();
    
    const [hasMore, setHasMore] = useState(true);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    
    const [inputText, setInputText] = useState('');
    const [showSettings, setShowSettings] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [templateList, setTemplateList] = useState<any[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
    const [newTagName, setNewTagName] = useState('');
    const [resSearchQuery, setResSearchQuery] = useState('');
    const [resSearchResults, setResSearchResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isLinking, setIsLinking] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [showScrollBottom, setShowScrollBottom] = useState(false);
    const [enlargedMedia, setEnlargedMedia] = useState<{ type: string; url: string } | null>(null); // { type, url }

    const [menuAnchor, setMenuAnchor] = useState<{ mouseX: number; mouseY: number } | null>(null);
    const [selectedMsgForMenu, setSelectedMsgForMenu] = useState<any>(null);
    const [quotingMessage, setQuotingMessage] = useState<any>(null);
    const [notification, setNotification] = useState<{ open: boolean; message: string; severity: string }>({ open: false, message: '', severity: 'success' });

    // ── エージェント機能 ───────────────────────────────────────────
    const [agentLoading, setAgentLoading]           = useState<string | null>(null); // 'check'|'change'|'cancel'
    const [agentResult, setAgentResult]             = useState<any | null>(null);    // 予約確認結果
    const [showChangeDialog, setShowChangeDialog]   = useState(false);
    const [changeNewReturn, setChangeNewReturn]     = useState('');
    const [changeResult, setChangeResult]           = useState<any | null>(null);
    const [cancelPreview, setCancelPreview]         = useState<any | null>(null);
    const [cancelConfirming, setCancelConfirming]   = useState(false);
    const [aiSuggestion, setAiSuggestion] = useState<{ reply: string; action_type: string; confidence: number; reasoning: string } | null>(null);
    const [isSuggesting, setIsSuggesting] = useState(false);
    // ─────────────────────────────────────────────────────────────

    // 翻訳機能 — 2軸: 受信は常に日本語翻訳、送信は顧客言語に翻訳
    // 顧客の言語（自動検出 or 手動オーバーライド）— per-chat
    const [detectedLang, setDetectedLang] = useState<string>('');
    // 送信時自動翻訳ON/OFF
    const [sendAutoTranslate, setSendAutoTranslate] = useState<boolean>(() => {
        try { return localStorage.getItem('lineChat_sendAutoTranslate') === '1'; } catch { return false; }
    });
    // 送信先言語（デフォルト = detectedLang）
    const [sendTranslateLang, setSendTranslateLang] = useState<string>('');
    // 送信先言語を手動オーバーライドしたか
    const [sendLangManualOverride, setSendLangManualOverride] = useState<boolean>(false);
    const [translatingIds, setTranslatingIds] = useState<Set<string | number>>(new Set());

    // 翻訳言語オプション（日本語除外 — 顧客言語用）
    const TRANSLATE_LANG_OPTIONS: { value: string; label: string; flag: string }[] = [
        { value: 'en', label: '英語', flag: '🇺🇸' },
        { value: 'ko', label: '韓国語', flag: '🇰🇷' },
        { value: 'zh', label: '中国語', flag: '🇨🇳' },
        { value: 'zh-tw', label: '繁体字', flag: '🇹🇼' },
        { value: 'fr', label: 'フランス語', flag: '🇫🇷' },
        { value: 'de', label: 'ドイツ語', flag: '🇩🇪' },
        { value: 'es', label: 'スペイン語', flag: '🇪🇸' },
        { value: 'th', label: 'タイ語', flag: '🇹🇭' },
        { value: 'vi', label: 'ベトナム語', flag: '🇻🇳' },
    ];
    // フラグ検索用（日本語含む）
    const ALL_LANG_OPTIONS = [{ value: 'ja', label: '日本語', flag: '🇯🇵' }, ...TRANSLATE_LANG_OPTIONS];

    // ポーリング通知用: 最後に確認したinboundメッセージのIDをRefで追跡（staleクロージャ対策）
    const latestKnownMsgIdRef = useRef<number | string | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isReadyToRenderMedia, setIsReadyToRenderMedia] = useState(false);
    
    // Shared date utilities
    const parseDate = useCallback((dateStr: string | null | undefined): Date | null => {
        if (!dateStr || typeof dateStr !== 'string') return null;
        
        // 1. ISO 8601 (2025-05-10T10:00:00) 
        if (dateStr.includes('T')) {
            const d = new Date(dateStr);
            if (!isNaN(d.getTime())) return d;
        }
        
        // 2. MySQL / General (2025-05-10 10:00:00)
        const cleaned = dateStr.replace(/-/g, '/');
        const d2 = new Date(cleaned);
        if (!isNaN(d2.getTime())) return d2;

        return null;
    }, []);

    const formatDateTime = useCallback((dtStr: any): string => {
        const dt = parseDate(dtStr);
        if (!dt) return dtStr || '';
        
        const mm = String(dt.getMonth() + 1).padStart(2, '0');
        const dd = String(dt.getDate()).padStart(2, '0');
        const hh = String(dt.getHours()).padStart(2, '0');
        const min = String(dt.getMinutes()).padStart(2, '0');
        
        return `${mm}/${dd} ${hh}:${min}`;
    }, [parseDate]);
    
    const formatTime = useCallback((dtStr: any): string => {
        const dt = parseDate(dtStr);
        if (!dt) return dtStr || '';
        const hh = String(dt.getHours()).padStart(2, '0');
        const min = String(dt.getMinutes()).padStart(2, '0');
        return `${hh}:${min}`;
    }, [parseDate]);
    
    const openTemplates = Boolean(anchorEl);
    const currentUser = users.find(u => u.line_user_id === lineUserId);
    const userMessages = messages[lineUserId] || [];
    const localOptimistic = optimisticMessages[lineUserId] || [];
    const allMessages = [...userMessages, ...localOptimistic];

    useEffect(() => {
        if (users.length === 0) fetchUsers();
        fetchTags();
    }, [users.length, fetchUsers, fetchTags]);

    // スクロール制御をシンプルに：マウント後に一度だけ最下部へ（画像なし状態での安定化のため）
    useEffect(() => {
        if (!isInitialLoading && !isReadyToRenderMedia && scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            
            // レイアウトが安定した後（プレースホルダー表示後）に画像を有効化
            // プレースホルダーが300pxを確保しているため、読み込み後のズレが最小限になります
            const timer = setTimeout(() => {
                setIsReadyToRenderMedia(true);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isInitialLoading, isReadyToRenderMedia]);

    const prevUserIdRef = useRef(null);

    // 日本語文字（ひらがな・カタカナ・漢字）を含むか判定
    const containsJapanese = (text: string) =>
        /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/.test(text);
    // 確実に日本語（ひらがな・カタカナ含む）— CJK漢字のみは中国語の可能性があるため除外
    const isDefinitelyJapanese = (text: string) =>
        /[\u3040-\u309F\u30A0-\u30FF]/.test(text);

    // detectedLang 初期化: lineUserId変更時にper-chat設定を復元
    useEffect(() => {
        if (!lineUserId) return;
        setSendLangManualOverride(false);
        // 1. localStorage（per-chat）から復元
        try {
            const saved = localStorage.getItem(`lineChat_detectedLang_${lineUserId}`);
            if (saved) { setDetectedLang(saved); return; }
        } catch {}
        // 2. currentUser.detected_language から（DB上のユーザー言語）
        const userLang = currentUser?.detected_language;
        if (userLang && userLang !== 'ja') { setDetectedLang(userLang); return; }
        // 3. 最新のinboundメッセージのdetected_languageから
        const latestForeign = allMessages.find(m => m.direction === 'inbound' && m.detected_language && m.detected_language !== 'ja');
        if (latestForeign?.detected_language) { setDetectedLang(latestForeign.detected_language); return; }
        // 4. リセット（自動検出に任せる）
        setDetectedLang('');
    }, [lineUserId, currentUser?.detected_language]);

    // detectedLang → sendTranslateLang 同期
    useEffect(() => {
        if (!lineUserId) return;
        // 手動オーバーライドの復元を確認
        try {
            const savedSendLang = localStorage.getItem(`lineChat_sendTranslateLang_${lineUserId}`);
            if (savedSendLang) {
                setSendTranslateLang(savedSendLang);
                setSendLangManualOverride(true);
                return;
            }
        } catch {}
        // detectedLangに追従
        if (detectedLang) {
            setSendTranslateLang(detectedLang);
        }
    }, [detectedLang, lineUserId]);

    // 受信自動翻訳: 外国語のinboundメッセージを常に日本語に翻訳
    useEffect(() => {
        if (!lineUserId) return;
        const untranslated = allMessages.filter(
            m => m.direction === 'inbound' && m.type === 'text'
            && !translatingIds.has(m.id)
            // 日本語メッセージはスキップ（detected_language優先、なければひらがな/カタカナで判定）
            && !(m.detected_language === 'ja')
            && !(!m.detected_language && isDefinitelyJapanese(m.content || ''))
            && !((m as any).translated_lang === 'ja' && m.translated_content)  // 既に日本語翻訳済みならスキップ
        );
        if (untranslated.length === 0) return;
        untranslated.forEach(msg => {
            setTranslatingIds(prev => new Set(prev).add(msg.id));
            translateMessage(msg.id, lineUserId, 'ja').then(result => {
                // 検出言語をdetectedLangに反映（未設定の場合のみ）
                if (result?.detected_language && result.detected_language !== 'ja') {
                    setDetectedLang(prev => {
                        if (!prev) {
                            try { localStorage.setItem(`lineChat_detectedLang_${lineUserId}`, result.detected_language!); } catch {}
                            return result.detected_language!;
                        }
                        return prev;
                    });
                }
            }).finally(() => {
                setTranslatingIds(prev => { const s = new Set(prev); s.delete(msg.id); return s; });
            });
        });
    }, [allMessages.length, lineUserId, translateMessage, translatingIds]);

    // userMessages が変わるたびに latestKnownMsgIdRef を最新に更新（staleクロージャ対策）
    useEffect(() => {
        const inbound = userMessages.filter(m => m.direction === 'inbound');
        if (inbound.length > 0) {
            const topId = inbound[inbound.length - 1].id;
            // ユーザー切り替え時はリセット
            if (prevUserIdRef.current !== lineUserId) {
                latestKnownMsgIdRef.current = topId;
            } else if (latestKnownMsgIdRef.current === null) {
                latestKnownMsgIdRef.current = topId;
            }
        }
    }, [userMessages, lineUserId]);

    useEffect(() => {
        if (lineUserId) {
            // カレントユーザーが変わった時だけ初期ロード状態にする
            if (prevUserIdRef.current !== lineUserId) {
                setIsInitialLoading(true);
                setIsReadyToRenderMedia(false); // ユーザー切り替え時はメディア読み込みをリセット
                latestKnownMsgIdRef.current = null; // ユーザー切り替え時にリセット
                prevUserIdRef.current = lineUserId;

                fetchMessages(lineUserId, null, 100).then(res => {
                    setHasMore(res.has_more);
                    setIsInitialLoading(false);
                });
            }

            if (readMode) {
                markRead(lineUserId);
            }

            const interval = setInterval(async () => {
                // 初期ロード中や追加読み込み中はポーリングを一時待機（混乱を避けるため）
                if (!isInitialLoading && !isLoadingMore) {
                    const res = await fetchMessages(lineUserId, null, 20);
                    // 個別チャット中に新着メッセージがあれば音を鳴らす（自分が送ったものではない場合）
                    // latestKnownMsgIdRef を使うことでstaleクロージャを回避
                    if (res && res.messages && res.messages.length > 0) {
                        const latestMsg = res.messages[res.messages.length - 1];
                        const knownId = latestKnownMsgIdRef.current;
                        if (latestMsg.direction === 'inbound' && knownId !== null && latestMsg.id > knownId) {
                            playNotificationSound();
                        }
                        // Refを最新に更新
                        if (latestMsg.direction === 'inbound') {
                            latestKnownMsgIdRef.current = latestMsg.id;
                        }
                    }
                }
            }, 10000);

            return () => clearInterval(interval);
        }
    }, [lineUserId, fetchMessages, markRead, readMode, isInitialLoading, isLoadingMore]);

    // Pusher: listen for real-time messages for this user
    useEffect(() => {
        if (!lineUserId) return;
        const handler = (e) => {
            const data = e.detail;
            if (data.line_user_id !== lineUserId) return;
            // Refresh messages from server to get the full record
            fetchMessages(lineUserId, null, 20);
            // Auto mark-read if readMode is on
            if (readMode) markRead(lineUserId);
        };
        window.addEventListener('line-pusher-message', handler);
        return () => window.removeEventListener('line-pusher-message', handler);
    }, [lineUserId, fetchMessages, readMode, markRead]);

    useEffect(() => {
        if (currentUser) {
            const userTagIds = currentUser.tags ? currentUser.tags.map(t => parseInt(t.id)) : [];
            setSelectedTagIds(userTagIds);
        } else if (user?.tags) {
            setSelectedTagIds(user.tags.map(t => parseInt(t.id)));
        }
    }, [currentUser, user]);

    useEffect(() => {
        // 新着メッセージ（自分が送信したもの）が来た時は、明示的に最下部へスクロールさせる
        if (localOptimistic.length > 0 && scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [localOptimistic.length]);

    const handleScroll = async () => {
        if (!scrollRef.current) return;
        
        // 下端に戻るボタンの表示判定 (最新から10個分ほど離れたら表示。1000px目安)
        // hasMore の状態に関わらず、常に計算・表示制御を行う
        const distFromBottom = scrollRef.current.scrollHeight - scrollRef.current.scrollTop - scrollRef.current.clientHeight;
        setShowScrollBottom(distFromBottom > 1000);

        if (isLoadingMore || !hasMore) return;
        
        if (scrollRef.current.scrollTop === 0) {
            const firstMsg = userMessages[0];
            if (!firstMsg) return;
            
            setIsLoadingMore(true);
            const scrollHeightBefore = scrollRef.current.scrollHeight;
            
            const res = await fetchMessages(lineUserId, String(firstMsg.id), 100);
            setHasMore(res.has_more);
            
            // スクロール位置を維持
            setTimeout(() => {
                if (scrollRef.current) {
                    scrollRef.current.scrollTop = scrollRef.current.scrollHeight - scrollHeightBefore;
                }
                setIsLoadingMore(false);
            }, 50);
        }
    };

    const handleOpenTemplates = async (event) => {
        setAnchorEl(event.currentTarget);
        const [templates, cats] = await Promise.all([
            fetchTemplates(),
            fetchTemplateCategories()
        ]);
        setTemplateList(templates);
        setCategories(cats);
    };

    const handleCloseTemplates = () => {
        setAnchorEl(null);
    };

    // Template Variable Parser
    const parseTemplate = (content: string): string => {
        if (!currentUser) return content;
        
        // Use the specific reservation linked to this chat instance from cache
        const targetResId = reservationId || (currentUser?.linked_reservations ? currentUser.linked_reservations[0]?.reservation_id : null);
        const linkedRes = targetResId ? reservationSyncService.getReservation(targetResId) : null;

        const vars = {
            customer_name: linkedRes?.customer_name || (currentUser || user)?.custom_display_name || (currentUser || user)?.line_display_name || '',
            start_date: linkedRes ? formatDateTime(linkedRes.start_date) : '',
            end_date: linkedRes ? formatDateTime(linkedRes.end_date) : '',
            start_date_time: linkedRes ? formatTime(linkedRes.start_date) : '',
            end_date_time: linkedRes ? formatTime(linkedRes.end_date) : '',
            start_time: linkedRes ? formatTime(linkedRes.start_date) : '',
            end_time: linkedRes ? formatTime(linkedRes.end_date) : '',
            id: linkedRes ? linkedRes.id : '',
            form: linkedRes ? `https://n-car.jp/sys/form/?id=${linkedRes.id}` : 'https://n-car.jp/sys/form/',
            status: linkedRes ? (linkedRes.status_name || linkedRes.status || '') : ''
        };

        let result = content;

        // 1. Handle IF logic with ELSIF and ELSE
        const ifRegex = /\{%\s*if\s+([a-zA-Z0-9_]+)\s*(==|!=)\s*([^\s%\}]+)\s*%\}([\s\S]*?)\{%\s*endif\s*%\}/gi;
        
        result = result.replace(ifRegex, (match, varName, op, val, fullInner) => {
            const currentVal = String(vars[varName.toLowerCase()] || '');
            const compareVal = val.trim().replace(/['"]/g, '');
            
            const parts = fullInner.split(/\{%\s*(elseif|else).*?%\}/i);
            const matches = fullInner.match(/\{%\s*(elseif|else)\s*([a-zA-Z0-9_]*)\s*(==|!=)?\s*([^\s%\}]*)?\s*%\}/gi) || [];
            
            let condition = (op === '==') ? (currentVal === compareVal) : (currentVal !== compareVal);
            if (condition) return parts[0];

            for (let i = 0; i < matches.length; i++) {
                const tag = matches[i];
                const content = parts[i + 1];
                
                if (tag.toLowerCase().includes('elseif')) {
                    const m = tag.match(/elseif\s+([a-zA-Z0-9_]+)\s*(==|!=)\s*([^\s%\}]+)/i);
                    if (m) {
                        const eVar = m[1].toLowerCase();
                        const eOp = m[2];
                        const eVal = m[3].trim().replace(/['"]/g, '');
                        const ecVal = String(vars[eVar] || '');
                        if ((eOp === '==') ? (ecVal === eVal) : (ecVal !== eVal)) {
                            return content;
                        }
                    }
                } else if (tag.toLowerCase().includes('else')) {
                    return content;
                }
            }
            return '';
        });

        Object.keys(vars).forEach(key => {
            const regex = new RegExp(`{${key}}`, 'g');
            result = result.replace(regex, vars[key]);
        });

        return result;
    };

    const handleSelectTemplate = (template: any) => {
        const parsed = parseTemplate(template.content);
        setInputText(parsed);
        handleCloseTemplates();
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files[0];
        if (!file) return;
        
        setIsUploading(true);
        try {
            await uploadImage(lineUserId, file);
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const toggleTag = (tagId: number) => {
        setSelectedTagIds(prev => 
            prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
        );
    };

    const handleAddTagMaster = async () => {
        if (!newTagName.trim()) return;
        const success = await addTagMaster(newTagName);
        if (success) setNewTagName('');
    };

    const handleResSearch = () => {
        if (resSearchQuery.length < 2) {
            setResSearchResults([]);
            return;
        }
        setIsSearching(true);
        const allReservations = reservationSyncService.getReservations();
        const query = resSearchQuery.toLowerCase();
        
        const filtered = allReservations.filter(r => 
            (r.customer_name && r.customer_name.toLowerCase().includes(query)) ||
            (r.id && r.id.toString().includes(query)) ||
            (r.contact_code && r.contact_code.toLowerCase().includes(query)) ||
            (r.external_id && r.external_id.toString().includes(query))
        ).slice(0, 50);
        
        const results = filtered.map(r => ({
            reservation_id: r.id,
            customer_name: r.customer_name,
            contact_code: r.contact_code,
            pickup_date: r.start_date || '',
            return_date: r.end_date || '',
            status: r.status_name || r.status || 'active',
            status_code: r.status || r.status_code || 'booked',
            status_name: r.status_name || r.status || '予約済み'
        }));
        
        setResSearchResults(results);
        setIsSearching(false);
    };

    const handleDeepSearch = async () => {
        setIsSearching(true);
        try {
            await reservationSyncService.syncAll();
            // 同期後に再度検索を実行
            handleResSearch();
        } catch (error) {
            console.error('Deep Search failed:', error);
            alert('全データの取得に失敗しました。');
        } finally {
            setIsSearching(false);
        }
    };

    const handleLink = async (rid: any) => {
        if (isLinking) return;
        const resData = resSearchResults.find(r => r.reservation_id === rid);
        setIsLinking(true);
        try {
            const success = await linkReservation(lineUserId, rid, resData);
            if (success) {
                setResSearchResults(prev => prev.filter(r => r.reservation_id !== rid));
                if (onReload) onReload();
            }
        } finally {
            setIsLinking(false);
        }
    };

    const handleUnlink = async (rid: any) => {
        // Remove confirm per user request
        const success = await unlinkReservation(lineUserId, rid);
        if (success) {
            setNotification({ open: true, message: '紐付けを解除しました', severity: 'success' });
            if (onReload) onReload();
        } else {
            setNotification({ open: true, message: '解除に失敗しました', severity: 'error' });
        }
    };

    const handleSend = async () => {
        if (!inputText.trim()) return;
        const currentText = inputText;
        const quoteToken = quotingMessage?.quote_token || null;
        let quotedContent = null;
        if (quotingMessage) {
            quotedContent = quotingMessage.type === 'text' ? quotingMessage.content : `[${quotingMessage.type || 'メッセージ'}]`;
        }
        setInputText('');
        setQuotingMessage(null);

        // 送信時自動翻訳ON → 選択言語に翻訳して送信
        if (sendAutoTranslate && sendTranslateLang && sendTranslateLang !== 'ja') {
            const result = await sendTranslatedMessage(lineUserId, currentText, sendTranslateLang, quoteToken, quotedContent, reservationId);
            if (result && !result.success) {
                setNotification({ open: true, message: result.error || '翻訳送信に失敗しました', severity: 'error' });
            }
            return;
        }

        const result = await sendMessage(lineUserId, currentText, quoteToken, quotedContent, reservationId);
        if (result && !result.success) {
            setNotification({ open: true, message: result.error || '送信に失敗しました', severity: 'error' });
        }
    };

    const handleSaveSettings = async () => {
        const success = await updateProfile(lineUserId, { 
            tag_ids: selectedTagIds
        });
        if (success) {
            setShowSettings(false);
        } else {
            alert('保存に失敗しました。');
        }
    };

    const handleMessageContextMenu = (event: React.MouseEvent, msg: any) => {
        event.preventDefault();
        setMenuAnchor({ mouseX: event.clientX - 2, mouseY: event.clientY - 4 });
        setSelectedMsgForMenu(msg);
    };

    const handleCloseMenu = () => {
        setMenuAnchor(null);
        setSelectedMsgForMenu(null);
    };

    const handleQuote = (msg?: any) => {
        const targetMsg = msg || selectedMsgForMenu;
        if (!targetMsg) return;
        
        setQuotingMessage(targetMsg);
        
        // 公式引用トークンがない場合のみ、便宜上テキスト冒頭に引用マークを入れる
        if (!targetMsg.quote_token) {
            const displayContent = targetMsg.content || `[${targetMsg.type || 'メッセージ'}]`;
            const quoteText = `> ${displayContent}\n\n`;
            setInputText(prev => quoteText + prev);
        }
        
        handleCloseMenu();
        
        // タイミング調整のために setTimeout を使用
        setTimeout(() => {
            if (textareaRef.current) {
                textareaRef.current.focus();
            }
        }, 100);
    };


    return (
        <div className="line-chat-window no-header">
            {/* Customer Link & Tags Area */}
            <div style={{ 
                background: '#f8fafc', 
                padding: '12px',
                borderBottom: '1px solid #e2e8f0',
                fontSize: '13px'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showSettings ? '12px' : '0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                        <div className="line-avatar-container">
                            <Avatar 
                                src={currentUser?.line_picture_url} 
                                alt="" 
                                sx={{ 
                                    width: '32px', 
                                    height: '32px', 
                                    borderRadius: '50%', 
                                    border: '1px solid #e2e8f0'
                                }} 
                            />
                            <div className={`link-status-badge ${currentUser?.linked_reservations?.length > 0 ? 'connected' : 'disconnected'}`}>
                                {currentUser?.linked_reservations?.length > 0 ? <LinkIcon sx={{ fontSize: 'inherit' }} /> : <LinkOffIcon sx={{ fontSize: 'inherit' }} />}
                            </div>
                        </div>
                        <div className="line-primary-name-row">
                            <span style={{ 
                                fontWeight: 800, 
                                color: '#1e293b',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '200px'
                            }}>
                                {instanceResData?.customer_name || currentUser?.custom_display_name || currentUser?.line_display_name}
                            </span>
                            
                            {(() => {
                                if (String(reservationId).startsWith('STAFF:')) {
                                    const staffName = String(reservationId).replace('STAFF:', '');
                                    return (
                                        <span style={{ 
                                            background: '#f1f5f9', 
                                            color: '#475569',
                                            fontSize: '11px',
                                            padding: '2px 10px',
                                            borderRadius: '6px',
                                            marginLeft: '12px',
                                            fontWeight: 800,
                                            border: '1px solid #e2e8f0',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            height: '22px'
                                        }}>
                                            スタッフ: {staffName}
                                        </span>
                                    );
                                }
                                const res = instanceResData || (reservationId ? reservationSyncService.getReservation(reservationId) : null);
                                if (!res) return null;
                                return (
                                    <span style={{ 
                                        background: res.bg_color || '#e2e8f0', 
                                        color: res.text_color || '#475569',
                                        fontSize: '10px',
                                        padding: '1px 8px',
                                        borderRadius: '4px',
                                        marginLeft: '8px',
                                        fontWeight: 800,
                                        whiteSpace: 'nowrap',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        height: '18px',
                                        border: '1px solid rgba(0,0,0,0.05)'
                                    }}>
                                        {res.status_name || res.status}
                                    </span>
                                );
                            })()}
                            
                            <div className="line-name-tags" style={{ display: 'inline-flex', marginLeft: '4px' }}>
                                {currentUser?.linked_reservations?.length > 1 && (
                                    <div className="line-mini-badge" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}>
                                        他予約有
                                    </div>
                                )}
                                {currentUser?.reservation_statuses?.map((st, idx) => {
                                    const classMap = {
                                        '本日貸出': 'today-pickup',
                                        '本日返却': 'today-return',
                                        '明日貸出': 'tomorrow-pickup',
                                        '明日返却': 'tomorrow-return',
                                        '利用中': 'in-use'
                                    };
                                    const cls = classMap[st] || st.toLowerCase().replace(/\s+/g, '-');
                                    return (
                                        <div key={idx} className={`line-mini-badge ${cls}`}>
                                            {st}
                                        </div>
                                    );
                                })}
                            </div>

                            {(() => {
                                // スタッフチャットの場合は予約詳細を表示しない
                                if (String(reservationId).startsWith('STAFF:')) return null;

                                const res = instanceResData || (reservationId ? reservationSyncService.getReservation(reservationId) : null);
                                if (!res) return null;
                                
                                const pStr = formatDateTime(res.pickup_date || res.start_date);
                                const rStr = formatDateTime(res.return_date || res.end_date);
                                const isPaid = res.payment === true || res.payment === 1 || res.payment === '1';
                                
                                return (
                                    <span style={{ 
                                        fontSize: '11px', 
                                        color: '#64748b', 
                                        marginLeft: '8px',
                                        fontWeight: 500,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}>
                                        {pStr} - {rStr}
                                        <span style={{ 
                                            color: isPaid ? '#10b981' : '#ef4444', 
                                            fontWeight: 700,
                                            marginLeft: '4px'
                                        }}>
                                            ({isPaid ? '支払済' : '未払い'})
                                        </span>
                                        {(() => {
                                            const res = instanceResData || (reservationId ? reservationSyncService.getReservation(reservationId) : null);
                                            const isFormOk = res?.agreement === true || res?.agreement === 1 || res?.agreement === '1';
                                            return (
                                                <span style={{
                                                    color: isFormOk ? '#10b981' : '#94a3b8',
                                                    fontWeight: 700,
                                                    marginLeft: '4px'
                                                }}>
                                                    [{isFormOk ? 'フォームOK' : 'フォーム未対応'}]
                                                </span>
                                            );
                                        })()}
                                        {(() => {
                                            const res = instanceResData || (reservationId ? reservationSyncService.getReservation(reservationId) : null);
                                            const licenseVal = res?.license ?? res?.detail?.license;
                                            const isLicense = licenseVal === true || licenseVal === 1 || licenseVal === '1';
                                            return (
                                                <span
                                                    style={{
                                                        color: isLicense ? '#10b981' : '#94a3b8',
                                                        fontWeight: 700,
                                                        marginLeft: '4px',
                                                        cursor: 'pointer',
                                                        userSelect: 'none',
                                                    }}
                                                    title="クリックで切替"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        if (!res?.id) return;
                                                        const { saveDispatchReservation } = await import('../api/dispatchService');
                                                        const newVal = isLicense ? 0 : 1;
                                                        await saveDispatchReservation({ id: res.id, license: newVal });
                                                    }}
                                                >
                                                    [{isLicense ? '免許確認済' : '免許未確認'}]
                                                </span>
                                            );
                                        })()}
                                    </span>
                                );
                            })()}
                        </div>
                        
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                            {currentUser?.tags?.map(tag => (
                                <span 
                                    key={tag.id} 
                                    className="line-tag" 
                                    style={{ 
                                        fontSize: '10px', 
                                        padding: '1px 6px', 
                                        background: tag.color + '22', 
                                        color: tag.color,
                                        border: `1px solid ${tag.color}44`
                                    }}
                                >
                                    {tag.name}
                                </span>
                            ))}
                        </div>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Tooltip title="アーカイブする">
                            <IconButton
                                size="small"
                                onClick={async () => {
                                    const success = await toggleArchive(lineUserId, true);
                                    if (success && onClose) onClose();
                                }}
                                sx={{
                                    color: '#94a3b8',
                                    '&:hover': { color: '#f59e0b', background: '#fef3c7' }
                                }}
                            >
                                <ArchiveIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>
                        <button
                            onClick={() => setShowSettings(!showSettings)}
                            style={{
                                background: showSettings ? '#f1f5f9' : '#eef2ff',
                                border: '1px solid #e2e8f0',
                                color: '#4f46e5',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontWeight: 700,
                                fontSize: '12px'
                            }}
                        >
                            {showSettings ? '閉じる' : '顧客紐付け・タグ編集'}
                        </button>
                    </div>
                </div>
                
                {showSettings && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', background: 'white', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        
                        {/* 紐付け済み予約一覧 */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <span style={{ fontWeight: 700 }}>紐付け済み予約:</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {(currentUser || user)?.linked_reservations?.length > 0 ? (
                                    (currentUser || user).linked_reservations.map(res => {
                                        // reservationSyncServiceのみから予約データ取得（DBキャッシュ不使用）
                                        const detailedRes = reservationSyncService.getReservation(res.reservation_id);
                                        const pickup = detailedRes?.start_date;
                                        const retDate = detailedRes?.end_date;
                                        const customerName = detailedRes?.customer_name || '';
                                        const statusName = detailedRes?.status_name || detailedRes?.status || '';

                                        return (
                                            <div key={res.reservation_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span style={{ fontWeight: 700, fontSize: '12px' }}>ID: {res.reservation_id}{customerName ? ` / ${customerName} 様` : ''}</span>
                                                    {pickup && retDate ? (
                                                        <span style={{ fontSize: '11px', color: '#64748b' }}>{formatDateTime(pickup)} 〜 {formatDateTime(retDate)}{statusName ? ` (${statusName})` : ''}</span>
                                                    ) : (
                                                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>日時データ取得中...</span>
                                                    )}
                                                </div>
                                                <IconButton size="small" onClick={() => handleUnlink(res.reservation_id)}>
                                                    <DeleteIcon style={{ fontSize: '16px', color: '#ef4444' }} />
                                                </IconButton>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>紐付けられた予約はありません</div>
                                )}
                            </div>
                        </div>

                        {/* 予約検索と紐付け */}
                        <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '8px' }}>
                            <span style={{ fontWeight: 700 }}>予約を検索して追加:</span>
                            <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                                <input 
                                    value={resSearchQuery}
                                    onChange={(e) => setResSearchQuery(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleResSearch()}
                                    placeholder="名前、ID、コードで検索"
                                    style={{ flex: 1, padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px' }}
                                />
                                <button onClick={handleResSearch} style={{ padding: '4px 12px', borderRadius: '6px', background: '#4f46e5', color: 'white', border: 'none', cursor: 'pointer', fontSize: '12px' }}>
                                    {isSearching ? '検索中...' : '検索'}
                                </button>
                            </div>
                            
                            {resSearchQuery.length >= 2 && !isSearching && (
                                <div style={{ marginTop: '8px', textAlign: 'center' }}>
                                    {resSearchResults.length === 0 && (
                                        <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>
                                            現在の表示範囲内に予約が見つかりません
                                        </div>
                                    )}
                                    <button 
                                        onClick={handleDeepSearch}
                                        style={{ 
                                            padding: '4px 12px', 
                                            borderRadius: '6px', 
                                            background: '#f8fafc', 
                                            color: '#4f46e5', 
                                            border: '1px solid #4f46e5', 
                                            cursor: 'pointer', 
                                            fontSize: '11px',
                                            fontWeight: 700
                                        }}
                                    >
                                        全データ取得
                                    </button>
                                </div>
                            )}
                            {resSearchResults.length > 0 && (
                                <div style={{ 
                                    marginTop: '8px', 
                                    maxHeight: '150px', 
                                    overflowY: 'auto', 
                                    border: '1px solid #e2e8f0', 
                                    borderRadius: '6px',
                                    opacity: isLinking ? 0.6 : 1,
                                    pointerEvents: isLinking ? 'none' : 'auto'
                                }}>
                                    {resSearchResults.map(res => (
                                        <div 
                                            key={res.reservation_id} 
                                            onClick={() => handleLink(res.reservation_id)}
                                            onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                                            onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                                            style={{ 
                                                padding: '6px 10px', 
                                                borderBottom: '1px solid #f1f5f9', 
                                                cursor: 'pointer',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                transition: 'background 0.2s'
                                            }}
                                        >
                                            <div>
                                                <div style={{ fontWeight: 700, fontSize: '12px' }}>
                                                    {res.customer_name} 様 (ID: {res.reservation_id})
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#64748b' }}>
                                                    {formatDateTime(res.pickup_date)} 〜 {res.contact_code && `(${res.contact_code})`} [{res.status}]
                                                </div>
                                            </div>
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center', 
                                                width: '24px', 
                                                height: '24px', 
                                                borderRadius: '50%', 
                                                background: '#f1f5f9' 
                                            }}>
                                                <AddIcon style={{ fontSize: '16px', color: '#4f46e5' }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {isLinking && <div style={{ fontSize: '11px', color: '#4f46e5', textAlign: 'center', marginTop: '4px' }}>紐付け中...</div>}
                        </div>

                        {/* タグ選択 */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #f1f5f9', paddingTop: '8px' }}>
                            <span style={{ fontWeight: 700 }}>タグを選択:</span>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', padding: '10px', background: '#f8fafc', borderRadius: '8px' }}>
                                {tags.map(tag => (
                                    <div 
                                        key={tag.id}
                                        onClick={() => toggleTag(parseInt(tag.id as any))}
                                        style={{
                                            padding: '4px 12px',
                                            borderRadius: '20px',
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            background: selectedTagIds.includes(parseInt(tag.id as any)) ? tag.color : '#fff',
                                            color: selectedTagIds.includes(parseInt(tag.id as any)) ? '#fff' : tag.color,
                                            border: `1px solid ${tag.color}`,
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {tag.name}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={{ borderTop: '1px dashed #e2e8f0', paddingTop: '10px' }}>
                            <span style={{ fontWeight: 700, fontSize: '12px', color: '#64748b' }}>タグマスタ管理:</span>
                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                                {tags.map(tag => (
                                    <div key={tag.id} style={{ display: 'flex', alignItems: 'center', background: '#f1f5f9', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>
                                        {tag.name}
                                        <IconButton size="small" onClick={() => deleteTagMaster(tag.id)} style={{ padding: '2px', marginLeft: '4px' }}>
                                            <DeleteIcon style={{ fontSize: '14px', color: '#ef4444' }} />
                                        </IconButton>
                                    </div>
                                ))}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input 
                                        value={newTagName}
                                        onChange={(e) => setNewTagName(e.target.value)}
                                        placeholder="新タグ名"
                                        style={{ padding: '2px 8px', borderRadius: '4px', border: '1px solid #cbd5e1', fontSize: '11px', width: '80px' }}
                                    />
                                    <IconButton size="small" onClick={handleAddTagMaster} style={{ background: '#4f46e5', color: 'white', padding: '2px' }}>
                                        <AddIcon style={{ fontSize: '14px' }} />
                                    </IconButton>
                                </div>
                            </div>
                        </div>

                        <button 
                            onClick={handleSaveSettings}
                            style={{ 
                                background: '#4f46e5', 
                                color: 'white', 
                                border: 'none', 
                                padding: '8px', 
                                borderRadius: '6px', 
                                fontWeight: 800, 
                                cursor: 'pointer',
                                marginTop: '4px'
                            }}
                        >
                            保存する
                        </button>
                    </div>
                )}
                
                {showScrollBottom && (
                    <IconButton 
                        onClick={() => {
                            if (scrollRef.current) {
                                scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
                            }
                        }}
                        sx={{
                            position: 'absolute',
                            bottom: '85px',
                            right: '25px',
                            backgroundColor: 'rgba(100, 116, 139, 0.6)',
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(100, 116, 139, 0.9)' },
                            zIndex: 1000,
                            width: '36px',
                            height: '36px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                        }}
                    >
                        <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                )}
            </div>

            <div className="line-message-list" ref={scrollRef} onScroll={handleScroll}>
                {(isInitialLoading || (userMessages.length === 0 && !isReadyToRenderMedia)) && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '12px', color: '#64748b' }}>
                        <CircularProgress size={32} sx={{ color: '#94a3b8' }} />
                        <span style={{ fontSize: '13px' }}>メッセージを読み込み中...</span>
                    </div>
                )}
                <div ref={contentRef} style={{ display: (isInitialLoading || (userMessages.length === 0 && !isReadyToRenderMedia)) ? 'none' : 'flex', flexDirection: 'column', gap: '12px' }}>
                    {isLoadingMore && (
                        <div style={{ textAlign: 'center', padding: '10px', color: '#64748b', fontSize: '12px' }}>
                            過去のメッセージを読み込み中...
                        </div>
                    )}
                    {isReadyToRenderMedia && userMessages.length === 0 && !isInitialLoading && (
                        <div style={{ textAlign: 'center', color: '#94a3b8', marginTop: '20px', fontSize: '14px' }}>
                            メッセージはありません
                        </div>
                    )}
                    {allMessages.map(msg => (
                        <div 
                            key={msg.id} 
                            id={`msg-${msg.line_message_id || msg.id}`}
                            className={`line-message-bubble ${msg.direction} ${msg.isOptimistic ? 'optimistic' : ''} type-${msg.type}`}
                            onContextMenu={(e) => handleMessageContextMenu(e, msg)}
                        >
                            <div className="message-content" style={{ whiteSpace: 'pre-wrap', position: 'relative' }}>
                                {msg.quoted_content && (
                                    <div 
                                        onClick={async () => {
                                            const findAndJump = (msgs) => {
                                                // 1. quote_token で検索 (アウトバウンドからインバウンドへの引用)
                                                // 2. line_message_id で検索 (インバウンドからアウトバウンド/インバウンドへの引用)
                                                const originalMsg = msgs.find(m => 
                                                    (msg.direction === 'outbound' && m.direction === 'inbound' && m.quote_token === msg.quote_token) ||
                                                    (msg.quoted_message_id && (m.line_message_id === msg.quoted_message_id || m.id == msg.quoted_message_id))
                                                );
                                                
                                                if (originalMsg) {
                                                    const originalId = `msg-${originalMsg.line_message_id || originalMsg.id}`;
                                                    const el = document.getElementById(originalId);
                                                    if (el) {
                                                        el.scrollIntoView({ behavior: 'auto', block: 'center' }); // 速度重視で auto
                                                        const container = (el.closest('.message-bubble') || el) as any;
                                                        const oldBg = container.style.backgroundColor;
                                                        container.style.transition = 'background-color 0.2s';
                                                        container.style.backgroundColor = 'rgba(255, 255, 0, 0.4)';
                                                        setTimeout(() => { container.style.backgroundColor = oldBg; }, 1500);
                                                        return true;
                                                    }
                                                }
                                                return false;
                                            };

                                            if (!findAndJump(allMessages)) {
                                                const contextMsgs = await fetchMessageContext(lineUserId, msg.quote_token, msg.quoted_message_id);
                                                if (contextMsgs) {
                                                    setTimeout(() => findAndJump([...allMessages, ...contextMsgs]), 100);
                                                } else {
                                                    alert('引用元のメッセージが見つかりませんでした（古いメッセージのため削除された可能性があります）');
                                                }
                                            }
                                        }}
                                        style={{ 
                                            background: 'rgba(0,0,0,0.15)', // 背景を少し濃くして視認性向上
                                            padding: '10px', 
                                            borderRadius: '12px', 
                                            marginBottom: '10px', 
                                            borderLeft: '4px solid #fff',
                                            fontSize: '13px',
                                            color: 'rgba(255,255,255,0.95)',
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                            backdropFilter: 'blur(4px)'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                            <div style={{ 
                                                width: '24px', 
                                                height: '24px', 
                                                borderRadius: '50%', 
                                                background: '#cbd5e1', 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center',
                                                fontSize: '10px',
                                                overflow: 'hidden'
                                            }}>
                                                {currentUser?.picture_url ? (
                                                    <img src={(currentUser || user)?.picture_url || (currentUser || user)?.line_picture_url} alt="" style={{ width: '100%', height: '100%' }} />
                                                ) : (
                                                    <span style={{ color: '#fff' }}>User</span>
                                                )}
                                            </div>
                                            <div style={{ fontWeight: 800 }}>{currentUser?.display_name || 'ユーザー'}</div>
                                        </div>
                                        <div style={{ 
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            opacity: 0.9,
                                            lineHeight: '1.4'
                                        }}>
                                            {msg.quoted_content ? msg.quoted_content : '引用メッセージ'}
                                        </div>
                                    </div>
                                )}
                                {msg.type === 'image' ? (
                                    isReadyToRenderMedia ? (
                                        <LazyImage 
                                            src={msg.direction === 'outbound' && msg.content.startsWith('http') 
                                                ? msg.content 
                                                : `${process.env.REACT_APP_LINE_API_URL || 'https://booking.trailernchu.co.jp/sys/line/api_v2.php'}?action=get_message_content&message_id=${msg.line_message_id || msg.id}&api_key=nc-line-sync-key-2024`
                                            } 
                                            alt="LINE Image" 
                                            style={{ 
                                                cursor: 'pointer', 
                                                maxWidth: '300px', 
                                                maxHeight: '300px', 
                                                borderRadius: '8px', 
                                                display: 'block',
                                                objectFit: 'contain'
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const src = msg.direction === 'outbound' && msg.content.startsWith('http') 
                                                    ? msg.content 
                                                    : `${process.env.REACT_APP_LINE_API_URL || 'https://booking.trailernchu.co.jp/sys/line/api_v2.php'}?action=get_message_content&message_id=${msg.line_message_id || msg.id}&api_key=nc-line-sync-key-2024`;
                                                setEnlargedMedia({ type: 'image', url: src });
                                            }}
                                        />
                                    ) : (
                                        <div style={{ width: '300px', height: '300px', padding: '20px', background: '#f1f5f9', borderRadius: '8px', color: '#64748b', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                            [image]
                                        </div>
                                    )
                                ) : msg.type === 'video' ? (
                                    isReadyToRenderMedia ? (
                                        <div style={{ position: 'relative', cursor: 'pointer' }} onClick={(e) => {
                                            e.stopPropagation();
                                                                                        const LINE_API_URL = process.env.REACT_APP_LINE_API_URL || 'https://booking.trailernchu.co.jp/sys/line/api_v2.php';
                                            const API_KEY = 'nc-line-sync-key-2024';
                                            const src = `${LINE_API_URL}?action=get_message_content&message_id=${msg.line_message_id || msg.id}&api_key=${API_KEY}`;
                                            setEnlargedMedia({ type: 'video', url: src });
                                        }}>
                                            <video 
                                                src={`${process.env.REACT_APP_LINE_API_URL || 'https://booking.trailernchu.co.jp/sys/line/api_v2.php'}?action=get_message_content&message_id=${msg.line_message_id || msg.id}&api_key=nc-line-sync-key-2024`}
                                                className="line-message-video"
                                                preload="metadata"
                                                style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px', display: 'block', objectFit: 'contain' }}
                                            />
                                            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'rgba(0,0,0,0.5)', borderRadius: '50%', width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                                                <div style={{ width: 0, height: 0, borderTop: '10px solid transparent', borderBottom: '10px solid transparent', borderLeft: '15px solid white', marginLeft: '5px' }} />
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ width: '300px', height: '300px', padding: '20px', background: '#f1f5f9', borderRadius: '8px', color: '#64748b', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                            [video]
                                        </div>
                                    )
                                ) : (
                                    <>
                                        {/* 送信翻訳: outbound で translated_content がある場合は送信テキスト(翻訳文)を先に表示 */}
                                        {msg.direction === 'outbound' && msg.translated_content ? (
                                            <>
                                                <div style={{ fontSize: '13px', lineHeight: 1.6 }}>{msg.translated_content}</div>
                                                <div style={{
                                                    marginTop: '6px',
                                                    padding: '5px 8px',
                                                    background: 'rgba(255,255,255,0.15)',
                                                    borderRadius: '6px',
                                                    fontSize: '11px',
                                                    color: 'rgba(255,255,255,0.8)',
                                                    borderLeft: '3px solid rgba(255,255,255,0.4)',
                                                }}>
                                                    <span style={{ fontWeight: 700, fontSize: '10px', opacity: 0.7 }}>日本語原文</span>
                                                    <div style={{ marginTop: '2px' }}>{msg.content}</div>
                                                </div>
                                            </>
                                        ) : (
                                            msg.content
                                        )}

                                        {/* 受信翻訳: inbound テキストの翻訳表示（日本語メッセージは対象外） */}
                                        {msg.direction === 'inbound' && msg.type === 'text' && (msg.detected_language ? msg.detected_language !== 'ja' : !containsJapanese(msg.content || '')) && (
                                            <div style={{ marginTop: '6px' }}>
                                                {msg.translated_content ? (
                                                    <div style={{
                                                        padding: '6px 10px',
                                                        background: '#f0fdf4',
                                                        borderRadius: '8px',
                                                        fontSize: '12px',
                                                        color: '#166534',
                                                        borderLeft: '3px solid #86efac',
                                                        lineHeight: 1.5,
                                                    }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                            {(() => {
                                                                const srcLang = msg.detected_language?.toUpperCase() || '?';
                                                                const tgtLang = ((msg as any).translated_lang || 'ja').toUpperCase();
                                                                const srcOpt = ALL_LANG_OPTIONS.find(l => l.value.toLowerCase() === (msg.detected_language || '').toLowerCase());
                                                                return (
                                                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#15803d' }}>
                                                                        {srcOpt?.flag || '🌐'} {srcLang} → {tgtLang}
                                                                    </span>
                                                                );
                                                            })()}
                                                            <Tooltip title="日本語に再翻訳">
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => {
                                                                            setTranslatingIds(prev => new Set(prev).add(msg.id));
                                                                            translateMessage(msg.id, lineUserId, 'ja', true).then(result => {
                                                                                if (!result) {
                                                                                    setNotification({ open: true, message: '再翻訳に失敗しました', severity: 'error' });
                                                                                }
                                                                            }).finally(() => {
                                                                                setTranslatingIds(prev => { const s = new Set(prev); s.delete(msg.id); return s; });
                                                                            });
                                                                        }}
                                                                        disabled={translatingIds.has(msg.id)}
                                                                        sx={{
                                                                            p: '2px',
                                                                            color: '#15803d',
                                                                            opacity: translatingIds.has(msg.id) ? 0.4 : 0.6,
                                                                            '&:hover': { opacity: 1, bgcolor: '#dcfce7' },
                                                                        }}
                                                                    >
                                                                        <AutorenewIcon sx={{ fontSize: '14px' }} />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </div>
                                                        <div style={{ marginTop: '2px' }}>{msg.translated_content}</div>
                                                    </div>
                                                ) : !msg.isOptimistic && (
                                                    <button
                                                        onClick={() => {
                                                            setTranslatingIds(prev => new Set(prev).add(msg.id));
                                                            translateMessage(msg.id, lineUserId, 'ja').then(result => {
                                                                if (!result) {
                                                                    setNotification({ open: true, message: '翻訳に失敗しました', severity: 'error' });
                                                                }
                                                            }).finally(() => {
                                                                setTranslatingIds(prev => { const s = new Set(prev); s.delete(msg.id); return s; });
                                                            });
                                                        }}
                                                        disabled={translatingIds.has(msg.id)}
                                                        style={{
                                                            background: 'none',
                                                            border: '1px solid #cbd5e1',
                                                            borderRadius: '12px',
                                                            padding: '2px 10px',
                                                            fontSize: '11px',
                                                            color: '#64748b',
                                                            cursor: translatingIds.has(msg.id) ? 'default' : 'pointer',
                                                            opacity: translatingIds.has(msg.id) ? 0.6 : 1,
                                                        }}
                                                    >
                                                        {translatingIds.has(msg.id) ? '翻訳中...' : '🌐 翻訳'}
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="message-time">
                                {new Date(msg.created_at).toLocaleDateString([], { month: '2-digit', day: '2-digit' })} {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                {msg.isOptimistic && (msg.status === 'sent' ? ' (送信済)' : ' (送信中...)')}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <Menu
                open={menuAnchor !== null}
                onClose={handleCloseMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    menuAnchor !== null
                        ? { top: menuAnchor.mouseY, left: menuAnchor.mouseX }
                        : undefined
                }
            >
                <MenuItem onClick={() => handleQuote()}>引用</MenuItem>
            </Menu>

            {/* ── エージェントパネル ────────────────────────────────── */}
            <div style={{ borderTop: '1px solid #e2e8f0', background: '#f8fafc', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* エージェントボタン行 */}
                <Box display="flex" gap={1} flexWrap="wrap">
                    {/* 予約確認 */}
                    <Tooltip title="紐づいている予約の詳細を確認">
                        <IconButton size="small"
                            disabled={!!agentLoading}
                            onClick={async () => {
                                setAgentLoading('check'); setAgentResult(null); setCancelPreview(null); setChangeResult(null);
                                const resId = currentUser?.linked_reservations?.[0]?.reservation_id ?? null;
                                const result = await agentCheckReservation(lineUserId, resId);
                                setAgentResult(result);
                                setAgentLoading(null);
                            }}
                            sx={{ bgcolor: '#eff6ff', color: '#2563eb', borderRadius: '8px', px: 1.5, py: 0.5, fontSize: '12px', fontWeight: 700, gap: '4px', '&:hover': { bgcolor: '#dbeafe' } }}
                        >
                            <span style={{ fontSize: '14px' }}>📋</span>
                            <span style={{ fontSize: '11px' }}>{agentLoading === 'check' ? '確認中...' : '予約確認'}</span>
                        </IconButton>
                    </Tooltip>

                    {/* 変更可否 */}
                    <Tooltip title="返却時刻の変更が可能かチェック">
                        <IconButton size="small"
                            disabled={!!agentLoading}
                            onClick={() => { setShowChangeDialog(true); setChangeResult(null); setChangeNewReturn(''); }}
                            sx={{ bgcolor: '#f0fdf4', color: '#16a34a', borderRadius: '8px', px: 1.5, py: 0.5, fontSize: '12px', fontWeight: 700, gap: '4px', '&:hover': { bgcolor: '#dcfce7' } }}
                        >
                            <span style={{ fontSize: '14px' }}>📅</span>
                            <span style={{ fontSize: '11px' }}>変更可否</span>
                        </IconButton>
                    </Tooltip>

                    {/* キャンセル */}
                    <Tooltip title="予約のキャンセルとキャンセル料の確認">
                        <IconButton size="small"
                            disabled={!!agentLoading}
                            onClick={async () => {
                                setAgentLoading('cancel'); setAgentResult(null); setCancelPreview(null); setChangeResult(null);
                                const resId = currentUser?.linked_reservations?.[0]?.reservation_id ?? null;
                                const result = await agentCancelReservation(lineUserId, resId, false);
                                setCancelPreview(result);
                                setAgentLoading(null);
                            }}
                            sx={{ bgcolor: '#fff1f2', color: '#e11d48', borderRadius: '8px', px: 1.5, py: 0.5, fontSize: '12px', fontWeight: 700, gap: '4px', '&:hover': { bgcolor: '#ffe4e6' } }}
                        >
                            <span style={{ fontSize: '14px' }}>🚫</span>
                            <span style={{ fontSize: '11px' }}>{agentLoading === 'cancel' ? '確認中...' : 'キャンセル'}</span>
                        </IconButton>
                    </Tooltip>

                    {/* 閉じるボタン（結果表示中のみ） */}
                    {(agentResult || cancelPreview || changeResult) && (
                        <IconButton size="small" onClick={() => { setAgentResult(null); setCancelPreview(null); setChangeResult(null); }}
                            sx={{ color: '#94a3b8', ml: 'auto' }}>
                            <CloseIcon style={{ fontSize: 16 }} />
                        </IconButton>
                    )}
                </Box>

                {/* 予約確認 結果カード */}
                {agentResult && (
                    <Box sx={{ background: '#fff', border: '1px solid #bfdbfe', borderRadius: '10px', p: 1.5, fontSize: '12px', lineHeight: 1.7 }}>
                        <Typography sx={{ fontWeight: 800, color: '#1e40af', fontSize: '13px', mb: 0.5 }}>
                            📋 予約 #{agentResult.reservation_id} — {agentResult.status_name}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px', color: '#334155' }}>
                            <span>👤 {agentResult.customer_name}</span>
                            <span>📞 {agentResult.customer_tel || '—'}</span>
                            <span>🚗 貸出: {agentResult.pickup_date}</span>
                            <span>🔁 返却: {agentResult.return_date}</span>
                            {agentResult.car_type && <span>🚙 {agentResult.car_type}</span>}
                            {agentResult.pax && <span>👥 {agentResult.pax}名</span>}
                            {agentResult.arrival_flight && <span>✈️ 到着: {agentResult.arrival_flight}</span>}
                            {agentResult.departure_flight && <span>✈️ 出発: {agentResult.departure_flight}</span>}
                            {agentResult.sum_price > 0 && <span>💴 合計: ¥{Number(agentResult.sum_price).toLocaleString()}</span>}
                        </Box>
                    </Box>
                )}

                {/* キャンセル プレビューカード */}
                {cancelPreview && !cancelConfirming && (
                    <Box sx={{ background: '#fff', border: '1px solid #fecdd3', borderRadius: '10px', p: 1.5, fontSize: '12px' }}>
                        <Typography sx={{ fontWeight: 800, color: '#be123c', fontSize: '13px', mb: 0.5 }}>🚫 キャンセル料の確認</Typography>
                        <Box sx={{ color: '#334155', lineHeight: 1.8 }}>
                            <div>予約: #{cancelPreview.reservation_id} — {cancelPreview.customer_name}</div>
                            <div>貸出日: {cancelPreview.pickup_date}</div>
                            <div>出発まで: <strong>{cancelPreview.days_until}日</strong></div>
                            <div>キャンセル料: <strong style={{ color: cancelPreview.percentage > 0 ? '#dc2626' : '#16a34a' }}>
                                {cancelPreview.label} → ¥{Number(cancelPreview.fee).toLocaleString()}
                            </strong></div>
                        </Box>
                        <Box display="flex" gap={1} mt={1}>
                            <button onClick={async () => {
                                setCancelConfirming(true);
                                const result = await agentCancelReservation(lineUserId, cancelPreview.reservation_id, true);
                                setCancelPreview(result ? { ...result, executed: true } : { ...cancelPreview, executed: false, error: true });
                                setCancelConfirming(false);
                            }} style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', padding: '6px 16px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
                                キャンセル実行
                            </button>
                            <button onClick={() => setCancelPreview(null)} style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', padding: '6px 16px', fontSize: '12px', cursor: 'pointer' }}>
                                閉じる
                            </button>
                        </Box>
                    </Box>
                )}
                {cancelPreview?.executed && (
                    <Box sx={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '10px', p: 1.5, fontSize: '12px', color: '#166534' }}>
                        ✅ キャンセルが完了しました（キャンセル料: ¥{Number(cancelPreview.fee).toLocaleString()}）
                    </Box>
                )}
                {cancelPreview?.error && (
                    <Box sx={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: '10px', p: 1.5, fontSize: '12px', color: '#be123c' }}>
                        ⚠️ キャンセル処理でエラーが発生しました。手動でご確認ください。
                    </Box>
                )}

                {/* 変更可否 結果カード */}
                {changeResult && (
                    <Box sx={{
                        background: '#fff',
                        border: `1px solid ${changeResult.auto_approve ? '#86efac' : '#fcd34d'}`,
                        borderRadius: '10px', p: 1.5, fontSize: '12px', color: '#334155', lineHeight: 1.8
                    }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '13px', mb: 0.5, color: changeResult.auto_approve ? '#166534' : '#92400e' }}>
                            {changeResult.auto_approve ? '✅' : '⚠️'} 変更可否結果
                        </Typography>
                        <div>{changeResult.reason}</div>
                        {changeResult.new_return && <div>新しい返却時刻: <strong>{changeResult.new_return}</strong></div>}
                    </Box>
                )}
            </div>

            {/* 変更可否 ダイアログ */}
            <Dialog open={showChangeDialog} onClose={() => setShowChangeDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 800, fontSize: '15px' }}>📅 返却時刻の変更可否確認</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 1.5, color: '#64748b' }}>
                        新しい返却日時を入力してください
                    </Typography>
                    <input
                        type="datetime-local"
                        value={changeNewReturn}
                        onChange={e => setChangeNewReturn(e.target.value)}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
                    />
                </DialogContent>
                <Box display="flex" gap={1} px={3} pb={2}>
                    <Button variant="contained" size="small" disabled={!changeNewReturn || agentLoading === 'change'}
                        onClick={async () => {
                            setAgentLoading('change');
                            const resId = currentUser?.linked_reservations?.[0]?.reservation_id ?? null;
                            const result = await agentCheckChange(lineUserId, changeNewReturn, resId);
                            setChangeResult(result);
                            setAgentLoading(null);
                            setShowChangeDialog(false);
                        }}
                        sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}>
                        {agentLoading === 'change' ? '確認中...' : '確認する'}
                    </Button>
                    <Button size="small" onClick={() => setShowChangeDialog(false)} sx={{ color: '#64748b' }}>閉じる</Button>
                </Box>
            </Dialog>

            <div className="line-input-area" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px', padding: '12px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                <Box display="flex" justifyContent="flex-start" alignItems="center" gap={1}>
                    <Tooltip title="メッセージテンプレート">
                        <IconButton 
                            size="small" 
                            onClick={handleOpenTemplates}
                            sx={{ 
                                bgcolor: '#f1f5f9', 
                                color: '#4f46e5',
                                '&:hover': { bgcolor: '#e2e8f0' },
                                borderRadius: '8px',
                                px: 1.5,
                                py: 0.5,
                                fontSize: '12px',
                                fontWeight: 700,
                                gap: '4px'
                            }}
                        >
                            <FlashOnIcon sx={{ fontSize: 16 }} />
                            テンプレート
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="画像をアップロード">
                        <IconButton 
                            size="small" 
                            onClick={() => fileInputRef.current?.click()}
                            sx={{ 
                                bgcolor: '#f1f5f9', 
                                color: '#64748b',
                                '&:hover': { bgcolor: '#e2e8f0' },
                                borderRadius: '8px'
                            }}
                        >
                            <span style={{ fontSize: '18px' }}>📎</span>
                        </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="テンプレートの書き方・ヘルプ">
                        <IconButton
                            size="small"
                            onClick={() => setShowHelp(true)}
                            sx={{
                                bgcolor: '#f1f5f9',
                                color: '#64748b',
                                '&:hover': { bgcolor: '#e2e8f0' },
                                borderRadius: '8px',
                                width: '32px',
                                height: '32px',
                                fontSize: '16px',
                                fontWeight: 700
                            }}
                        >
                            ?
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="直近のメッセージからAIが返信候補を生成します">
                        <IconButton
                            size="small"
                            disabled={isSuggesting}
                            onClick={async () => {
                                setIsSuggesting(true);
                                setAiSuggestion(null);
                                const result = await suggestReply(lineUserId);
                                setAiSuggestion(result);
                                setIsSuggesting(false);
                            }}
                            sx={{
                                bgcolor: '#ede9fe',
                                color: '#7c3aed',
                                '&:hover': { bgcolor: '#ddd6fe' },
                                '&:disabled': { bgcolor: '#f5f5f5', color: '#bbb' },
                                borderRadius: '8px',
                                px: 1.5,
                                py: 0.5,
                                fontSize: '12px',
                                fontWeight: 700,
                                gap: '4px',
                            }}
                        >
                            ✨ {isSuggesting ? '生成中...' : 'AI候補'}
                        </IconButton>
                    </Tooltip>

                    {/* Button 1: 顧客言語検出 */}
                    <Tooltip title="顧客の言語（自動検出 / 手動変更）">
                        <Box display="flex" alignItems="center" sx={{
                            bgcolor: detectedLang ? '#eff6ff' : '#f1f5f9',
                            border: detectedLang ? '1px solid #93c5fd' : '1px solid #e2e8f0',
                            borderRadius: '8px',
                            overflow: 'hidden',
                        }}>
                            <select
                                value={detectedLang}
                                onChange={e => {
                                    const val = e.target.value;
                                    setDetectedLang(val);
                                    try { localStorage.setItem(`lineChat_detectedLang_${lineUserId}`, val); } catch {}
                                }}
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    color: detectedLang ? '#2563eb' : '#94a3b8',
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    padding: '0 4px 0 8px',
                                    height: '32px',
                                    cursor: 'pointer',
                                    outline: 'none',
                                    maxWidth: '100px',
                                }}
                            >
                                <option value="">🌐 自動検出</option>
                                {TRANSLATE_LANG_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.flag} {opt.label}</option>
                                ))}
                            </select>
                        </Box>
                    </Tooltip>

                    {/* Button 2: 送信時自動翻訳ON/OFF + 送信先言語 */}
                    <Box display="flex" alignItems="center" sx={{
                        bgcolor: sendAutoTranslate ? '#dcfce7' : '#f1f5f9',
                        border: sendAutoTranslate ? '1px solid #86efac' : '1px solid #e2e8f0',
                        borderRadius: '8px',
                        overflow: 'hidden',
                    }}>
                        <Tooltip title={sendAutoTranslate ? `送信時に日本語→${ALL_LANG_OPTIONS.find(l => l.value === sendTranslateLang)?.label || sendTranslateLang}に翻訳` : '翻訳なしで送信'}>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    const next = !sendAutoTranslate;
                                    setSendAutoTranslate(next);
                                    try { localStorage.setItem('lineChat_sendAutoTranslate', next ? '1' : '0'); } catch {}
                                    // ON時にsendTranslateLangが未設定ならdetectedLangをセット
                                    if (next && !sendTranslateLang && detectedLang) {
                                        setSendTranslateLang(detectedLang);
                                    }
                                }}
                                sx={{
                                    bgcolor: 'transparent',
                                    color: sendAutoTranslate ? '#16a34a' : '#94a3b8',
                                    '&:hover': { bgcolor: sendAutoTranslate ? '#bbf7d0' : '#e2e8f0' },
                                    borderRadius: 0,
                                    px: 1.5,
                                    py: 0.5,
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    gap: '4px',
                                    minWidth: 0,
                                }}
                            >
                                <span style={{ fontSize: '14px' }}>🌐</span>
                                <span style={{ fontSize: '11px' }}>自動翻訳{sendAutoTranslate ? 'ON' : ''}</span>
                            </IconButton>
                        </Tooltip>
                        {sendAutoTranslate && (
                            <Tooltip title="送信先の言語を選択">
                                <select
                                    value={sendTranslateLang}
                                    onChange={e => {
                                        setSendTranslateLang(e.target.value);
                                        setSendLangManualOverride(true);
                                        try { localStorage.setItem(`lineChat_sendTranslateLang_${lineUserId}`, e.target.value); } catch {}
                                    }}
                                    style={{
                                        border: 'none',
                                        borderLeft: '1px solid #86efac',
                                        background: 'transparent',
                                        color: '#16a34a',
                                        fontSize: '11px',
                                        fontWeight: 700,
                                        padding: '0 4px 0 6px',
                                        height: '32px',
                                        cursor: 'pointer',
                                        outline: 'none',
                                        maxWidth: '80px',
                                    }}
                                >
                                    {TRANSLATE_LANG_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.flag} {opt.label}</option>
                                    ))}
                                </select>
                            </Tooltip>
                        )}
                    </Box>
                </Box>

                {aiSuggestion && (
                    <Box sx={{ bgcolor: '#faf5ff', border: '1px solid #c4b5fd', borderRadius: '10px', p: 1.5 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Typography variant="caption" sx={{ color: '#7c3aed', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                ✨ AI返信候補
                                {aiSuggestion.action_type === 'escalate' && (
                                    <span style={{ background: '#fee2e2', color: '#dc2626', fontSize: '10px', padding: '1px 6px', borderRadius: '4px', fontWeight: 800 }}>ESCALATE</span>
                                )}
                                <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 4 }}>({Math.round(aiSuggestion.confidence * 100)}%)</span>
                            </Typography>
                            <IconButton size="small" onClick={() => setAiSuggestion(null)} sx={{ color: '#7c3aed', p: 0.25 }}>
                                <CloseIcon style={{ fontSize: 14 }} />
                            </IconButton>
                        </Box>
                        <Typography variant="body2" sx={{ color: '#374151', whiteSpace: 'pre-wrap', mb: 1, lineHeight: 1.5 }}>
                            {aiSuggestion.reply}
                        </Typography>
                        <Box display="flex" justifyContent="flex-end">
                            <button
                                onClick={() => {
                                    setInputText(aiSuggestion.reply);
                                    setAiSuggestion(null);
                                    setTimeout(() => textareaRef.current?.focus(), 50);
                                }}
                                style={{ background: '#7c3aed', color: 'white', border: 'none', borderRadius: '6px', padding: '4px 12px', fontSize: '12px', cursor: 'pointer', fontWeight: 700 }}
                            >
                                入力欄に貼り付け
                            </button>
                        </Box>
                    </Box>
                )}

                {quotingMessage && (
                    <Box sx={{
                        bgcolor: '#eff6ff',
                        p: 1,
                        borderRadius: '8px',
                        border: '1px solid #bfdbfe',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Typography variant="caption" sx={{ color: '#1e40af', fontWeight: 700 }}>
                            引用中: {(quotingMessage.content || '').substring(0, 30)}{(quotingMessage.content || '').length > 30 ? '...' : ''}
                        </Typography>
                        <IconButton size="small" onClick={() => setQuotingMessage(null)} sx={{ color: '#1e40af' }}>
                            <CloseIcon style={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>
                )}

                <Box display="flex" gap={1.5} alignItems="flex-end">
                    <input 
                        type="file" 
                        ref={fileInputRef} 
                        style={{ display: 'none' }} 
                        accept="image/*"
                        onChange={handleImageUpload}
                    />

                    {isUploading && <span style={{ fontSize: '11px', color: '#4f46e5' }}>アップロード中...</span>}

                    <Menu
                        anchorEl={anchorEl}
                        open={openTemplates}
                        onClose={handleCloseTemplates}
                        sx={{ zIndex: 10600 }}
                        PaperProps={{
                            sx: {
                                width: '320px',
                                maxHeight: '400px',
                                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                borderRadius: '12px',
                                border: '1px solid #e2e8f0',
                                mt: 1,
                                overflowX: 'hidden'
                            }
                        }}
                        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
                    >
                        <Box px={2} py={1} borderBottom="1px solid #f1f5f9" bgcolor="#f8fafc">
                            <Typography variant="overline" fontWeight={800} color="textSecondary">テンプレートを選択</Typography>
                        </Box>
                        {templateList.length === 0 ? (
                            <MenuItem disabled sx={{ py: 2, justifyContent: 'center' }}>
                                <Typography variant="body2" color="textSecondary">テンプレートが登録されていません</Typography>
                            </MenuItem>
                        ) : (
                            [...categories, { id: null, name: '未分類' }].map(cat => {
                                const catTemplates = templateList.filter(t => (cat.id === null ? !t.category_id : t.category_id == cat.id));
                                if (catTemplates.length === 0) return null;

                                return [
                                    <Box key={`cat-head-${cat.id}`} px={2} py={0.5} sx={{ bgcolor: '#f1f5f9' }}>
                                        <Typography variant="caption" fontWeight={800} color="textSecondary">
                                            {cat.name}
                                        </Typography>
                                    </Box>,
                                    ...catTemplates.map(t => (
                                        <MenuItem 
                                            key={t.id} 
                                            onClick={() => handleSelectTemplate(t)}
                                            sx={{ 
                                                flexDirection: 'column', 
                                                alignItems: 'flex-start',
                                                py: 1.5,
                                                borderBottom: '1px solid #f8fafc',
                                                '&:last-child': { borderBottom: 'none' }
                                            }}
                                        >
                                            <Typography variant="subtitle2" fontWeight={800} color="primary">{t.name}</Typography>
                                            <Typography 
                                                variant="caption" 
                                                color="textSecondary" 
                                                sx={{ 
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    lineHeight: 1.4,
                                                    mt: 0.5
                                                }}
                                            >
                                                {t.content}
                                            </Typography>
                                        </MenuItem>
                                    ))
                                ];
                            })
                        )}
                    </Menu>
                </Box>
                
                <Box display="flex" gap={1.5} alignItems="flex-end" sx={{ mt: 1 }}>
                    <textarea 
                        ref={textareaRef}
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                                e.preventDefault();
                                handleSend();
                            }
                        }}
                        placeholder="メッセージを入力してください..."
                        className="line-chat-input"
                        rows={5}
                        style={{ 
                            flex: 1, 
                            padding: '10px 14px', 
                            borderRadius: '12px', 
                            border: '1px solid #e2e8f0', 
                            fontSize: '14px',
                            resize: 'none',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            lineHeight: '1.5',
                            outline: 'none'
                        }}
                    />
                    <button
                        onClick={handleSend}
                        disabled={!inputText.trim() || loading || isUploading}
                        style={{
                            background: inputText.trim()
                                ? ((sendAutoTranslate && sendTranslateLang && sendTranslateLang !== 'ja') ? '#d97706' : '#4f46e5')
                                : '#e2e8f0',
                            color: 'white',
                            border: 'none',
                            width: '40px',
                            height: '40px',
                            borderRadius: '12px',
                            cursor: inputText.trim() ? 'pointer' : 'default',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s',
                            flexShrink: 0,
                            fontSize: (sendAutoTranslate && sendTranslateLang && sendTranslateLang !== 'ja') ? '18px' : undefined,
                        }}
                        title={(sendAutoTranslate && sendTranslateLang && sendTranslateLang !== 'ja') ? `翻訳して送信 (→${ALL_LANG_OPTIONS.find(l => l.value === sendTranslateLang)?.label || sendTranslateLang})` : '送信'}
                    >
                        {(sendAutoTranslate && sendTranslateLang && sendTranslateLang !== 'ja') ? '🌐' : <FlashOnIcon style={{ fontSize: '20px' }} />}
                    </button>
                </Box>
            </div>

            {/* メディア拡大表示モーダル */}
            <Dialog
                open={Boolean(enlargedMedia)}
                onClose={() => setEnlargedMedia(null)}
                maxWidth="lg"
                sx={{ zIndex: 11000 }}
                PaperProps={{
                    style: {
                        background: 'transparent',
                        boxShadow: 'none',
                        overflow: 'hidden'
                    }
                }}
            >
                <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: '300px', minHeight: '300px' }}>
                    <IconButton 
                        onClick={(e) => {
                            e.stopPropagation();
                            setEnlargedMedia(null);
                        }}
                        style={{ position: 'absolute', top: 10, right: 10, color: 'white', background: 'rgba(0,0,0,0.5)', zIndex: 10 }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {enlargedMedia?.type === 'image' ? (
                        <img 
                            src={enlargedMedia.url} 
                            alt="LINE Full" 
                            style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain' }} 
                        />
                    ) : enlargedMedia?.type === 'video' ? (
                        <video 
                            src={enlargedMedia.url} 
                            controls 
                            autoPlay 
                            style={{ maxWidth: '95vw', maxHeight: '95vh' }} 
                        />
                    ) : null}
                </div>
            </Dialog>

            <TemplateHelpDialog 
                open={showHelp} 
                onClose={() => setShowHelp(false)} 
            />

            <Snackbar 
                open={notification.open} 
                autoHideDuration={4000} 
                onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={() => setNotification(prev => ({ ...prev, open: false }))} 
                    severity={notification.severity as any}
                    sx={{ width: '100%', boxShadow: 3 }}
                    variant="filled"
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default LineChatPanel;
